import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";

const BASE_URL = "https://api.playbro.com/api/slides";

// Live site CTA accent (frontend ka accent-blue actually orange hai).
const CTA_ACCENT = "#ef8402";

interface Slide {
  id: number;
  tag: string | null;
  title: string;
  description: string | null;
  cta: string | null;
  ctaLink: string | null;
  readMore: string | null;
  slug: string | null;
  bg: string | null; // absolute url
  image: string | null; // absolute url
  sortOrder: number;
}

interface FormState {
  tag: string;
  title: string;
  description: string;
  cta: string;
  ctaLink: string;
  readMore: string;
  slug: string;
  sortOrder: string;
  bgFile: File | null;
  imageFile: File | null;
  bgPreview: string; // objectURL (new file) or existing absolute url
  imagePreview: string;
}

const EMPTY_FORM: FormState = {
  tag: "",
  title: "",
  description: "",
  cta: "",
  ctaLink: "",
  readMore: "",
  slug: "",
  sortOrder: "0",
  bgFile: null,
  imageFile: null,
  bgPreview: "",
  imagePreview: "",
};

/* ── Live preview — HeroBanner jaisa look ───────────────────────── */
const SlidePreview: React.FC<{ form: FormState }> = ({ form }) => (
  <Box
    sx={{
      position: "relative",
      height: 200,
      borderRadius: 2,
      overflow: "hidden",
      bgcolor: "#03121A",
      px: 2,
      py: 2,
      border: "1px solid #1E2D55",
    }}
  >
    {/* bg image — faint */}
    {form.bgPreview && (
      <Box
        component="img"
        src={form.bgPreview}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.12,
          pointerEvents: "none",
        }}
      />
    )}
    {/* main image — right */}
    {form.imagePreview && (
      <Box
        component="img"
        src={form.imagePreview}
        alt=""
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          objectFit: "contain",
          objectPosition: "right",
          pointerEvents: "none",
        }}
      />
    )}
    <Box sx={{ position: "relative", maxWidth: "70%" }}>
      <Typography sx={{ fontSize: 13, color: "#8384A5", mb: 1 }}>
        {form.tag || "Tag"}
      </Typography>
      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 700,
          color: "#fff",
          textTransform: "uppercase",
          lineHeight: 1.05,
          mb: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {form.title || "Slide Title"}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "#C9CEDB",
          mb: 2,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {form.description || "Slide description goes here…"}
      </Typography>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: 40,
          px: 2,
          borderRadius: 2,
          bgcolor: CTA_ACCENT,
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {form.cta || "Call to action"}
      </Box>
    </Box>
  </Box>
);

const Banner: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null; all: boolean }>({
    open: false,
    id: null,
    all: false,
  });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = (message: string, severity: "success" | "error" = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/getAll`);
      if (data.status === "success") setSlides(data.slides || []);
      else notify("Failed to fetch slides", "error");
    } catch {
      notify("Failed to fetch slides", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    // Naya slide list ke end me append ho — unique sortOrder, koi collision nahi.
    setForm({ ...EMPTY_FORM, sortOrder: String(slides.length) });
    setDialogOpen(true);
  };

  const openEdit = (s: Slide) => {
    setEditingId(s.id);
    setForm({
      tag: s.tag || "",
      title: s.title || "",
      description: s.description || "",
      cta: s.cta || "",
      ctaLink: s.ctaLink || "",
      readMore: s.readMore || "",
      slug: s.slug || "",
      sortOrder: String(s.sortOrder ?? 0),
      bgFile: null,
      imageFile: null,
      bgPreview: s.bg || "",
      imagePreview: s.image || "",
    });
    setDialogOpen(true);
  };

  const handleField = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // Ek slide ka sortOrder backend pe set karo (sirf sortOrder bhejo — baaki fields/images preserve).
  const updateSortOrder = (id: number, order: number) => {
    const fd = new FormData();
    fd.append("sortOrder", String(order));
    return axios.post(`${BASE_URL}/update/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  // Up/down se reorder — array swap karke sabko clean 0..n-1 sortOrder de do (duplicates self-heal).
  const moveSlide = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (reordering || target < 0 || target >= slides.length) return;

    const reordered = [...slides];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    setSlides(reordered); // optimistic UI
    setReordering(true);
    try {
      // Sirf un slides ko persist karo jinka naya position purane sortOrder se alag hai.
      await Promise.all(
        reordered
          .map((s, i) => (s.sortOrder !== i ? updateSortOrder(s.id, i) : null))
          .filter(Boolean) as Promise<unknown>[]
      );
      await fetchSlides();
    } catch {
      notify("Failed to reorder", "error");
      await fetchSlides(); // revert to server truth
    } finally {
      setReordering(false);
    }
  };

  const handleFile = (which: "bg" | "image") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setForm((prev) =>
      which === "bg"
        ? { ...prev, bgFile: file, bgPreview: url }
        : { ...prev, imageFile: file, imagePreview: url }
    );
  };

  const saveSlide = async () => {
    if (!form.title.trim()) {
      notify("Title is required", "error");
      return;
    }
    const fd = new FormData();
    fd.append("tag", form.tag);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("cta", form.cta);
    fd.append("ctaLink", form.ctaLink);
    fd.append("readMore", form.readMore);
    fd.append("slug", form.slug);
    fd.append("sortOrder", form.sortOrder || "0");
    if (form.bgFile) fd.append("bg", form.bgFile);
    if (form.imageFile) fd.append("image", form.imageFile);

    setSaving(true);
    try {
      const url = editingId ? `${BASE_URL}/update/${editingId}` : `${BASE_URL}/create`;
      await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      notify(editingId ? "Slide updated" : "Slide created");
      setDialogOpen(false);
      await fetchSlides();
    } catch {
      notify("Failed to save slide", "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      if (confirmDelete.all) {
        await axios.delete(`${BASE_URL}/deleteAll`);
        notify("All slides deleted");
      } else if (confirmDelete.id != null) {
        await axios.delete(`${BASE_URL}/delete/${confirmDelete.id}`);
        notify("Slide deleted");
      }
      await fetchSlides();
    } catch {
      notify("Failed to delete", "error");
    } finally {
      setConfirmDelete({ open: false, id: null, all: false });
    }
  };

  const formValid = useMemo(() => form.title.trim().length > 0, [form.title]);

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
            Banner Management
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Hero promotional slides shown on the home page.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            disabled={isLoading || slides.length === 0}
            onClick={() => setConfirmDelete({ open: true, id: null, all: true })}
          >
            Delete All
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Slide
          </Button>
        </Stack>
      </Stack>

      {/* List */}
      {isLoading ? (
        <Stack spacing={2}>
          {Array.from(new Array(3)).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : slides.length === 0 ? (
        <Card sx={{ p: 6, textAlign: "center", border: "1px dashed #1E2D55" }}>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            No slides yet. Add your first promotional slide.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Slide
          </Button>
        </Card>
      ) : (
        <Stack spacing={2}>
          {slides.map((s, idx) => (
            <Card
              key={s.id}
              sx={{
                display: "flex",
                alignItems: "stretch",
                overflow: "hidden",
                borderRadius: 2,
                border: "1px solid #1E2D55",
                transition: "border-color .2s, transform .2s",
                "&:hover": { borderColor: "#2B6EF5", transform: "translateY(-1px)" },
              }}
            >
              {/* Thumb — full card height */}
              <Box
                sx={{
                  position: "relative",
                  width: 190,
                  minWidth: 190,
                  alignSelf: "stretch",
                  bgcolor: "#03121A",
                  display: { xs: "none", sm: "block" },
                }}
              >
                {s.bg && (
                  <Box
                    component="img"
                    src={s.bg}
                    alt=""
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.3 }}
                  />
                )}
                {s.image && (
                  <Box
                    component="img"
                    src={s.image}
                    alt={s.title}
                    sx={{ position: "absolute", right: 0, top: 0, height: "100%", objectFit: "contain", objectPosition: "right" }}
                  />
                )}
                {/* order badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {idx + 1}
                </Box>
              </Box>
              {/* Body */}
              <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: 1.75, "&:last-child": { pb: 1.75 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  {s.tag && (
                    <Chip
                      label={s.tag}
                      size="small"
                      sx={{ height: 22, bgcolor: "#10182E", color: "#5581F7", fontWeight: 600, border: "1px solid #1E2D55" }}
                    />
                  )}
                  {s.slug && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>/{s.slug}</Typography>}
                </Stack>
                <Typography sx={{ fontWeight: 700, fontSize: 17, color: "text.primary", lineHeight: 1.2 }}>{s.title}</Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25, mb: 1.25, maxWidth: 560 }} noWrap>
                  {s.description}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ fontSize: 12 }}>
                  {s.cta && (
                    <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "rgba(239,132,2,0.12)", color: "#EF8402", fontWeight: 600 }}>
                      {s.cta}
                    </Box>
                  )}
                  {s.ctaLink && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>→ {s.ctaLink}</Typography>}
                </Stack>
              </CardContent>
              {/* Reorder (up/down) */}
              <Stack direction="column" justifyContent="center" sx={{ px: 0.5, borderLeft: "1px solid #1E2D55" }}>
                <IconButton
                  size="small"
                  disabled={idx === 0 || reordering}
                  onClick={() => moveSlide(idx, -1)}
                  aria-label="move up"
                  sx={{ color: "#8384A5", "&:hover": { bgcolor: "rgba(85,129,247,0.12)", color: "#5581F7" } }}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === slides.length - 1 || reordering}
                  onClick={() => moveSlide(idx, 1)}
                  aria-label="move down"
                  sx={{ color: "#8384A5", "&:hover": { bgcolor: "rgba(85,129,247,0.12)", color: "#5581F7" } }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </Stack>
              {/* Actions */}
              <Stack direction="row" alignItems="center" sx={{ px: 1, borderLeft: "1px solid #1E2D55" }}>
                <IconButton
                  size="small"
                  onClick={() => openEdit(s)}
                  aria-label="edit"
                  sx={{ color: "#5581F7", "&:hover": { bgcolor: "rgba(85,129,247,0.12)" } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setConfirmDelete({ open: true, id: s.id, all: false })}
                  aria-label="delete"
                  sx={{ color: "#E01B4F", "&:hover": { bgcolor: "rgba(224,27,79,0.12)" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? "Edit Slide" : "Add Slide"}
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Preview */}
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>
            Preview
          </Typography>
          <SlidePreview form={form} />

          <Divider sx={{ my: 3 }} />

          {/* Fields */}
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Tag" fullWidth value={form.tag} onChange={handleField("tag")} placeholder="Deposits" />
              <TextField label="Slug" fullWidth value={form.slug} onChange={handleField("slug")} placeholder="twice-as-nice" />
            </Stack>
            <TextField label="Title" required fullWidth value={form.title} onChange={handleField("title")} placeholder="Twice as Nice" />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={handleField("description")}
              placeholder="For the next 30 days, one lucky deposit every day gets doubled by us"
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="CTA Text" fullWidth value={form.cta} onChange={handleField("cta")} placeholder="Deposit Now" />
              <TextField label="CTA Link" fullWidth value={form.ctaLink} onChange={handleField("ctaLink")} placeholder="/casino/categories/slots (blank = deposit modal)" />
            </Stack>
            <TextField label="Read More Link" fullWidth value={form.readMore} onChange={handleField("readMore")} placeholder="/promotions/twice-as-nice" />
            {/* Sort order ab list me up/down buttons se manage hota hai — manual field hata diya. */}

            {/* Image uploads */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Background Image (bg)</Typography>
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                  {form.bgFile ? form.bgFile.name : "Choose bg image"}
                  <input hidden type="file" accept=".png,.jpg,.jpeg,.webp,.avif,.gif" onChange={handleFile("bg")} />
                </Button>
                {form.bgPreview && (
                  <Box component="img" src={form.bgPreview} alt="bg" sx={{ mt: 1, width: "100%", height: 90, objectFit: "cover", borderRadius: 1, border: "1px solid #1E2D55" }} />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Main Image (image)</Typography>
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                  {form.imageFile ? form.imageFile.name : "Choose main image"}
                  <input hidden type="file" accept=".png,.jpg,.jpeg,.webp,.avif,.gif" onChange={handleFile("image")} />
                </Button>
                {form.imagePreview && (
                  <Box component="img" src={form.imagePreview} alt="main" sx={{ mt: 1, width: "100%", height: 90, objectFit: "contain", borderRadius: 1, border: "1px solid #1E2D55", bgcolor: "#03121A" }} />
                )}
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button onClick={saveSlide} disabled={!formValid || saving} variant="contained">
            {saving ? "Saving…" : editingId ? "Update Slide" : "Create Slide"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null, all: false })}>
        <DialogTitle>{confirmDelete.all ? "Delete ALL slides?" : "Delete this slide?"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            {confirmDelete.all
              ? "Saari slides permanently delete ho jayengi. Ye undo nahi hoga."
              : "Ye slide permanently delete ho jayegi."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null, all: false })} color="inherit">
            Cancel
          </Button>
          <Button onClick={doDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Banner;
